import { computed, toValue } from 'vue'
// types
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue'
import type { StrapiCollectionResponse, StrapiPage } from '@ts-strapi/interfaces'
// hooks
import { useStrapiFetch } from '@api/strapi/use-strapi-fetch.hook'

interface StrapiPageHookOptions {
  sectionTypes?: string[]
}

interface StrapiPageHookReturn {
  refPage: ComputedRef<StrapiPage | null>
  refPending: Ref<boolean>
  refError: Ref<Error | null>
  refresh: () => Promise<void>
}

// Page-level populate, always requested in the deep (second) phase.
const PAGE_POPULATE: Record<string, string> = {
  'populate[seo][populate]': '*',
  'populate[stickyBottomBar][populate][override][populate]': '*',
}

// Deep populate recipes per section type, keyed by the exact Strapi query key.
// Strapi v5 dynamic zones need the `on` syntax with a deep populate per
// component type; a wildcard can't be mixed with per-component depth. Listing
// every known type in ONE request made the URL grow with the catalog until it
// crossed the ingress `large_client_header_buffers` (8 KB) cap and every page
// fetch 414'd. So the fetch runs in two phases (see below) and this map is
// filtered down to only the section types a given page actually uses.
//
// When adding a new section to `StrapiPageSection`, add its populate keys here.
// A present type with no entry still renders: the second phase falls back to a
// shallow `populate=*` for it so the section is never dropped.
const SECTION_POPULATE: Record<string, string> = {
  'populate[sections][on][sections.banner-image][populate]': '*',
  'populate[sections][on][sections.banner-support][populate]': '*',
  'populate[sections][on][sections.hero-text][populate]': '*',
  'populate[sections][on][sections.cards-list][populate]': '*',
  'populate[sections][on][sections.related-articles-wp][populate]': '*',
  'populate[sections][on][sections.faq-multiple][populate][faq][populate][faqItem][populate]': '*',
  'populate[sections][on][sections.helpful][populate]': '*',
  'populate[sections][on][sections.card-image-list][populate][cards][populate]': '*',
  'populate[sections][on][sections.card-image-list][populate][layout][populate]': '*',
  'populate[sections][on][sections.faq-single][populate][layout][populate]': '*',
  'populate[sections][on][sections.faq-single][populate][items][populate]': '*',
  'populate[sections][on][sections.banner-hiring][populate][layout][populate]': '*',
  'populate[sections][on][sections.banner-hiring][populate][banner][populate][title][populate]': '*',
  'populate[sections][on][sections.banner-cta][populate][layout][populate]': '*',
  'populate[sections][on][sections.banner-cta][populate][banner][populate]': '*',
  'populate[sections][on][sections.banner-promo][populate][layout][populate]': '*',
  'populate[sections][on][sections.banner-promo][populate][banner][populate]': '*',
  'populate[sections][on][sections.reviews-slider][populate][layout][populate]': '*',
  'populate[sections][on][sections.reviews-slider][populate][reviews][populate]': '*',
  'populate[sections][on][sections.award-list][populate][layout][populate]': '*',
  'populate[sections][on][sections.award-list][populate][awards][populate]': '*',
  'populate[sections][on][sections.logo-scroller][populate][layout][populate]': '*',
  'populate[sections][on][sections.logo-scroller][populate][logos][populate]': '*',
  'populate[sections][on][sections.quote][populate][layout][populate]': '*',
  'populate[sections][on][sections.quote][populate][quote][populate][title][populate]': '*',
  'populate[sections][on][sections.quote][populate][quote][populate][image]': 'true',
  'populate[sections][on][sections.banner-cta-booking][populate][layout][populate]': '*',
  'populate[sections][on][sections.banner-cta-booking][populate][banner][populate]': '*',
  'populate[sections][on][sections.internal-link][populate][layout][populate]': '*',
  'populate[sections][on][sections.internal-link][populate][links][populate]': '*',
  'populate[sections][on][sections.tutors][populate][layout][populate]': '*',
  'populate[sections][on][sections.tutors][populate][tutors][populate][profile][populate]': '*',
  'populate[sections][on][sections.tutors][populate][tutors][populate][quote]': 'true',
  'populate[sections][on][sections.tutors][populate][tutors][populate][button]': 'true',
  'populate[sections][on][sections.blog-card][populate][layout][populate]': '*',
  'populate[sections][on][sections.blog-card][populate][cards][populate]': '*',
  'populate[sections][on][sections.hero-home][populate][title][populate][items][populate][shapeIcon][populate]': '*',
  'populate[sections][on][sections.hero-home][populate][gradeSelector][populate][grades][populate]': '*',
  'populate[sections][on][sections.hero-home][populate][stats][populate][items]': 'true',
  'populate[sections][on][sections.hero-image][populate][image]': 'true',
  'populate[sections][on][sections.hero-image][populate][mediaCard][populate]': '*',
  'populate[sections][on][sections.hero-image][populate][primaryCta]': 'true',
  'populate[sections][on][sections.hero-image][populate][secondaryCta]': 'true',
  'populate[sections][on][sections.hero-image][populate][tertiaryCta]': 'true',
  'populate[sections][on][sections.hero-image][populate][stats][populate][items]': 'true',
  'populate[sections][on][sections.subscription-plans][populate][layout][populate]': '*',
  'populate[sections][on][sections.subscription-plans][populate][plans][populate][features]': 'true',
  'populate[sections][on][sections.subscription-plans][populate][plans][populate][button]': 'true',
  'populate[sections][on][sections.subscription-plans][populate][trustItems]': 'true',
  'populate[sections][on][sections.ratings-gauge][populate][layout][populate]': '*',
  'populate[sections][on][sections.ratings-gauge][populate][criteria]': 'true',
  'populate[sections][on][sections.banner-compact][populate][layout][populate]': '*',
  'populate[sections][on][sections.banner-compact][populate][banner][populate]': '*',
  'populate[sections][on][sections.video-block][populate][layout][populate]': '*',
  'populate[sections][on][sections.video-block][populate][mediaCard][populate]': '*',
  'populate[sections][on][sections.video-block][populate][card]': 'true',
  'populate[sections][on][sections.highlights][populate][layout][populate]': '*',
  'populate[sections][on][sections.highlights][populate][cards][populate]': '*',
  'populate[sections][on][sections.review-stories][populate][layout][populate]': '*',
  'populate[sections][on][sections.review-stories][populate][stories][populate]': '*',
  'populate[sections][on][sections.features][populate][layout][populate]': '*',
  'populate[sections][on][sections.features][populate][cards][populate]': '*',
  'populate[sections][on][sections.banner-text-block][populate][layout][populate]': '*',
  'populate[sections][on][sections.banner-text-block][populate][banner][populate]': '*',
  'populate[sections][on][sections.reasons-list][populate][layout][populate]': '*',
  'populate[sections][on][sections.reasons-list][populate][reasons][populate]': '*',
  'populate[sections][on][sections.hero-grade][populate][title][populate][items][populate][shapeIcon][populate]': '*',
  'populate[sections][on][sections.hero-grade][populate][gradeSelector][populate][grades][populate]': '*',
  'populate[sections][on][sections.hero-grade][populate][stats][populate][items]': 'true',
  'populate[sections][on][sections.discipline-needs][populate][layout][populate]': '*',
  'populate[sections][on][sections.discipline-needs][populate][links][populate]': '*',
  'populate[sections][on][sections.grade-type][populate][layout][populate]': '*',
  'populate[sections][on][sections.grade-type][populate][grades][populate]': '*',
  'populate[sections][on][sections.application][populate][layout][populate]': '*',
  'populate[sections][on][sections.application][populate][instructions][populate]': '*',
  'populate[sections][on][sections.worksheets][populate][layout][populate]': '*',
  'populate[sections][on][sections.worksheets][populate][links][populate]': '*',
  'populate[sections][on][sections.test][populate][layout][populate]': '*',
  'populate[sections][on][sections.test][populate][tests][populate]': '*',
  'populate[sections][on][sections.impact-cards][populate][layout][populate]': '*',
  'populate[sections][on][sections.impact-cards][populate][mainCard][populate]': '*',
  'populate[sections][on][sections.impact-cards][populate][sideCards][populate]': '*',
        'populate[sections][on][sections.hero-cta][populate][title][populate][items][populate][shapeIcon][populate]': '*',
        'populate[sections][on][sections.hero-cta][populate][primaryCta]': 'true',
        'populate[sections][on][sections.hero-cta][populate][secondaryCta]': 'true',
        'populate[sections][on][sections.hero-cta][populate][tertiaryCta]': 'true',
        'populate[sections][on][sections.hero-cta][populate][stats][populate][items]': 'true',
        'populate[sections][on][sections.trust][populate][stats][populate][items]': 'true',
        'populate[sections][on][sections.rich-text][populate]': '*',
}

const sectionTypeOfKey = (key: string): string | null =>
  key.match(/\[on\]\[(sections\.[^\]]+)\]/)?.[1] ?? null

const MAPPED_SECTION_TYPES = new Set(
  Object.keys(SECTION_POPULATE)
    .map(sectionTypeOfKey)
    .filter((type): type is string => type !== null),
)

// Builds the deep-phase params for exactly the section types present on a page.
// Known types get their recipe; a present-but-unmapped type gets a shallow
// `populate=*` so it is still returned (a partial `on` list drops any type it
// omits). This caps the URL by the page's own section types, not the catalog.
const buildDeepParams = (slug: string, presentTypes: Set<string>): Record<string, string> => {
  const params: Record<string, string> = {
    'filters[slug][$eq]': slug,
    ...PAGE_POPULATE,
  }

  for (const [key, value] of Object.entries(SECTION_POPULATE)) {
    const type = sectionTypeOfKey(key)
    if (type && presentTypes.has(type)) params[key] = value
  }

  for (const type of presentTypes) {
    if (!MAPPED_SECTION_TYPES.has(type)) {
      params[`populate[sections][on][${type}][populate]`] = '*'
    }
  }

  return params
}

// Fetches a single Page by slug from Strapi v5 in two phases to keep the
// request URL small (see SECTION_POPULATE above for why):
//   1. Discovery — a shallow `populate[sections][populate]=*` returns every
//      section's `__component` discriminator with a tiny URL.
//   2. Deep — re-fetch the page populating only the section types discovered in
//      phase 1, so the URL scales with the page's own sections, not the catalog.
//      `sectionTypes` narrows phase 2 further to the given types (a partial `on`
//      list drops the others) — used by the section catalog preview, whose
//      `section-library` page holds every type and would otherwise exceed the
//      ingress URL limit.
// useAsyncData runs both phases once on the server during SSG/SSR and hydrates
// on the client without a refetch. When the slug changes (client navigation in
// [...slug].vue), the watcher re-runs the handler.
export const useStrapiPage = async (
  slug: MaybeRefOrGetter<string>,
  { sectionTypes }: StrapiPageHookOptions = {},
): Promise<StrapiPageHookReturn> => {
  const strapiFetch = useStrapiFetch()
  const refSlug = computed<string>(() => toValue(slug))
  const wantedTypes = sectionTypes ? new Set(sectionTypes) : null
  const keySuffix = sectionTypes ? `:${sectionTypes.join(',')}` : ''

  const { data, pending, error, refresh } = await useAsyncData(
    `strapi:page:${refSlug.value}${keySuffix}`,
    async () => {
      const discovery = await strapiFetch<StrapiCollectionResponse<StrapiPage>>('/pages', {
        params: {
          'filters[slug][$eq]': refSlug.value,
          'populate[sections][populate]': '*',
        },
      })

      const discoveredPage = discovery.data?.[0]
      if (!discoveredPage) return discovery

      const presentTypes = new Set<string>(
        (discoveredPage.sections ?? [])
          .map(section => section.__component)
          .filter(type => !wantedTypes || wantedTypes.has(type)),
      )

      return strapiFetch<StrapiCollectionResponse<StrapiPage>>('/pages', {
        params: buildDeepParams(refSlug.value, presentTypes),
      })
    },
    { watch: [refSlug] },
  )

  const refPage = computed<StrapiPage | null>(() => data.value?.data?.[0] ?? null)

  return {
    refPage,
    refPending: pending,
    refError: error as Ref<Error | null>,
    refresh: () => refresh(),
  }
}
